import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Image } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaSearch, FaTag } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const ReasonScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { tweet, isFromNotification } = location.state || {};

  const handleBack = () => {
    navigate(-1);
  };

  if (!tweet) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBack} style={styles.backButtonContainer}>
            <FaArrowLeft style={styles.backButtonIcon} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('app.reason.tweetReason')}</Text>
          <View style={{ width: 40 }} />
        </View>

        <View style={styles.emptyContainer}>
          <Image
            source={require('../assets/images/ReactFirstR.png')}
            style={styles.emptyImage}
            alt="ReactFirst AI Logo"
          />
          <Text style={styles.emptyTitle}>{t('app.reason.noTweetFound')}</Text>
          <Text style={styles.emptyText}>{t('app.reason.tweetNotAvailable')}</Text>
          <TouchableOpacity style={styles.returnButton} onPress={handleBack}>
            <Text style={styles.returnButtonText}>{t('app.reason.returnPrevious')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={handleBack} style={styles.backButtonContainer}>
          <FaArrowLeft style={styles.backButtonIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('app.reason.tweetInsight')}</Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView style={styles.content}>
        <View style={styles.tweetCard}>
          <View style={styles.authorContainer}>
            <View style={styles.authorAvatar}>
              {tweet.username && tweet.username.charAt(0).toUpperCase()}
            </View>
            <View style={styles.authorInfo}>
              <Text style={styles.authorName}>{tweet.author_name}</Text>
              {tweet.username && <Text style={styles.username}>@{tweet.username}</Text>}
            </View>
          </View>

          <Text style={styles.tweetContent}>{tweet.tweet_content}</Text>

          <View style={styles.tweetMeta}>
            <View style={styles.metaBadge}>
              <FaTag style={styles.metaBadgeIcon} />
              <Text style={styles.metaBadgeText}>{tweet.list_name || t('app.reason.list')}</Text>
            </View>
          </View>
        </View>

        <View style={styles.insightContainer}>
          <View style={styles.insightHeader}>
            <View style={styles.insightIcon}>
              <FaSearch style={styles.insightIconImage} />
            </View>
            <Text style={styles.insightTitle}>{t('app.reason.whyAppeared')}</Text>
          </View>

          <View style={styles.reasonCard}>
            <Text style={styles.reasonText}>{tweet.reason || t('app.reason.defaultReason')}</Text>
          </View>

          {tweet.dependencies && tweet.dependencies.length > 0 && (
            <View style={styles.dependencySection}>
              <Text style={styles.dependencyTitle}>{t('app.reason.relatedContent')}</Text>
              {tweet.dependencies.map((dependency, index) => (
                <View key={index} style={styles.dependencyCard}>
                  <Text style={styles.dependencyText}>{dependency}</Text>
                </View>
              ))}
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: 'var(--bg-deep-void)',
    backgroundImage: `
      radial-gradient(circle at 15% 50%, rgba(59, 130, 246, 0.08), transparent 25%),
      radial-gradient(circle at 85% 30%, rgba(139, 92, 246, 0.08), transparent 25%)
    `,
    position: 'relative',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 24,
    paddingHorizontal: 32,
    backgroundColor: 'rgba(5, 5, 7, 0.7)',
    backdropFilter: 'blur(20px)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.06)',
    zIndex: 50,
  },
  backButtonContainer: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  backButtonIcon: {
    color: 'var(--text-secondary)',
    fontSize: 14,
  },
  headerTitle: {
    color: 'var(--text-primary)',
    fontSize: 20,
    fontWeight: '700',
    fontFamily: 'Outfit, sans-serif',
    letterSpacing: '-0.02em',
    textShadow: '0 0 20px rgba(59, 130, 246, 0.3)',
  },
  content: {
    flex: 1,
    padding: 32,
    overflowY: 'auto',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyImage: {
    width: 80,
    height: 80,
    marginBottom: 24,
    opacity: 0.5,
  },
  emptyTitle: {
    color: 'var(--text-primary)',
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 12,
    fontFamily: 'Outfit, sans-serif',
  },
  emptyText: {
    color: 'var(--text-muted)',
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 32,
  },
  returnButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  returnButtonText: {
    color: 'var(--text-primary)',
    fontSize: 16,
    fontWeight: '600',
  },
  tweetCard: {
    backgroundImage: 'linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
    borderRadius: 24,
    padding: 32,
    marginBottom: 32,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(20px)',
  },
  authorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  authorAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundImage: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
  },
  authorInfo: {
    flex: 1,
  },
  authorName: {
    color: 'var(--text-primary)',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
    fontFamily: 'Outfit, sans-serif',
  },
  username: {
    color: 'var(--text-secondary)',
    fontSize: 14,
  },
  tweetContent: {
    color: 'var(--text-primary)',
    fontSize: 18,
    lineHeight: 28,
    marginBottom: 24,
    fontWeight: '400',
    fontFamily: 'Inter, sans-serif',
  },
  tweetMeta: {
    flexDirection: 'row',
    marginTop: 16,
  },
  metaBadge: {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.2)',
    flexDirection: 'row',
    alignItems: 'center',
  },
  metaBadgeIcon: {
    color: 'var(--accent-primary)',
    fontSize: 12,
    marginRight: 8,
  },
  metaBadgeText: {
    color: 'var(--accent-primary)',
    fontSize: 13,
    fontWeight: '600',
    letterSpacing: '0.02em',
  },
  insightContainer: {
    marginBottom: 40,
  },
  insightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  insightIcon: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: 'rgba(139, 92, 246, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 20,
    borderWidth: 1,
    borderColor: 'rgba(139, 92, 246, 0.2)',
  },
  insightIconImage: {
    color: 'var(--accent-secondary)',
    fontSize: 20,
  },
  insightTitle: {
    color: 'var(--text-primary)',
    fontSize: 24,
    fontWeight: '700',
    fontFamily: 'Outfit, sans-serif',
    letterSpacing: '-0.02em',
  },
  reasonCard: {
    backgroundColor: 'rgba(139, 92, 246, 0.05)',
    borderRadius: 24,
    padding: 32,
    borderLeftWidth: 4,
    borderLeftColor: 'var(--accent-secondary)',
    marginBottom: 32,
    borderWidth: 1,
    borderTopColor: 'rgba(139, 92, 246, 0.1)',
    borderRightColor: 'rgba(139, 92, 246, 0.1)',
    borderBottomColor: 'rgba(139, 92, 246, 0.1)',
  },
  reasonText: {
    color: 'var(--text-primary)',
    fontSize: 18,
    lineHeight: 28,
    fontWeight: '400',
  },
  dependencySection: {
    marginTop: 24,
  },
  dependencyTitle: {
    color: 'var(--text-primary)',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 16,
    fontFamily: 'Outfit, sans-serif',
  },
  dependencyCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: 16,
    padding: 20,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
    transition: 'all 0.2s ease',
  },
  dependencyText: {
    color: 'var(--text-secondary)',
    fontSize: 15,
    lineHeight: 24,
  },
});

export default ReasonScreen; 